import { unwrap } from '@/api/envelope';
import { useGetModerationHistoryByContent } from '@/api/generated/moderation/moderation';
import type { AdminModerationHistoryResponseDTO } from '@/api/generated/model';
import { ModerationHistoryList } from '@/components/ModerationHistoryList';
import {
  explainRiskBandCause,
  pickCombinedRisk,
  pickHistoryRiskIndicators,
} from '@/features/moderation/lib/combinedRiskDisplay';
import { shouldShowModerationHistoryEntry } from '../lib/contentDisplay';
import { useContentIntelligence } from '../api';

// Content moderation history (op #5, contentType=P). Enveloped (shape A) -> unwrap to the row array.
// Policy max + 12 Policy confidences (legacy safety fallback) from content-intelligence cache.
export function ModerationHistoryCard({
  contentId,
  combinedRisk,
  riskTier,
}: {
  contentId: string;
  /** Publication ledger risk — used to hide stuck TipMi escalate history (no Coop risk). */
  combinedRisk?: number | null;
  riskTier?: string | null;
}) {
  const id = Number(contentId);
  const valid = Number.isFinite(id) && id > 0;
  const query = useGetModerationHistoryByContent(
    id,
    { contentType: 'P' },
    {
      query: {
        enabled: valid,
        select: (res) => unwrap<AdminModerationHistoryResponseDTO[] | undefined>(res) ?? [],
      },
    },
  );
  const { data: intelligence } = useContentIntelligence(contentId);
  const riskIndicators = pickHistoryRiskIndicators(intelligence);
  // Detail has no ledger risk fields; Coop intelligence is the same fallback the overview strip uses.
  const intelligenceRisk = pickCombinedRisk(intelligence ?? undefined);
  const entries = (query.data ?? []).filter((entry) =>
    shouldShowModerationHistoryEntry(entry, {
      combinedRisk: combinedRisk ?? intelligenceRisk,
      riskTier,
    }),
  );

  return (
    <ModerationHistoryList
      entries={entries}
      isLoading={query.isLoading}
      isError={query.isError}
      onRetry={() => void query.refetch()}
      hideSubject
      riskIndicators={riskIndicators}
      riskCause={explainRiskBandCause(intelligence)}
    />
  );
}
